import React, { KeyboardEventHandler, MouseEventHandler } from 'react';
import clsx from 'clsx';
import Chip, { ChipProps } from '@material-ui/core/Chip';
import { ReactComponent as CancelIcon } from 'common-assets/assets/icons/cross-filled.svg';
import { ReactComponent as BinIcon } from 'common-assets/assets/icons/delete-filled.svg';
import { useTranslation } from 'react-i18next';
import { useFocusVisibleRef } from 'common-react';

import useStyles from './Chip.style';


export interface CustomChipProps extends Omit<ChipProps, 'variant'> {
  variant?: 'default' | 'clearFilters';
  className?: string;
  cancelIconAriaLabel?: string;
  testId?: string;
  autoTestId?: string;
}

const CustomChip: React.FC<CustomChipProps> = ({ className, cancelIconAriaLabel, testId = 'Chip', autoTestId, variant = 'default', onDelete, ...props }) => {
  const c = useStyles({ });
  const [t] = useTranslation('components');
  const { ref: focusVisibleRef, isFocusVisible } = useFocusVisibleRef<HTMLDivElement>();

  const handleClick: MouseEventHandler<HTMLDivElement> = event => {
    if (typeof onDelete === "function") {
      onDelete(event);
    }
  };

  const handleDeleteChipByEnterKey: KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === "Enter" && typeof onDelete === "function") {
      event.preventDefault();
      onDelete(event);
    }
  };

  const defaultCancelIconAriaLabel = props?.label
    ? t("chip.cancelIconAriaLabel", {
        label: props.label,
      })
    : undefined;

  const deleteIcon = variant === 'clearFilters'
    ? <span className={c.deleteIcon}><BinIcon aria-hidden="true" /></span>
    : <span className={c.deleteIcon}><CancelIcon aria-hidden="true" /></span>;
  return (
    <Chip
      ref={focusVisibleRef}
      data-testid={testId}
      data-autoTestId={autoTestId}
      aria-label={cancelIconAriaLabel || defaultCancelIconAriaLabel}
      onClick={handleClick}
      onKeyDown={handleDeleteChipByEnterKey}
      onDelete={onDelete}
      classes={{
        deleteIcon: c.deleteIcon,
        root: clsx(c.root, {
          [c.focusVisible]: isFocusVisible,
          [c.clearFilters]: variant === 'clearFilters'
        }),
        label: c.label,
      }}
      deleteIcon={deleteIcon}
      className={clsx(className)}
      {...props}
    />
  );
};

export {CustomChip as Chip} ;
